import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { InternalEventType, WsMessage } from '../../../clients/chat/types';
import { getFileNameOrTextMessage } from '../../../components/plugins/chat/MessageList';
import { getSelectedTabKeyFromUrl } from '../../../store/childStore/tabStore';
import { useCurrentChatDispatch } from '../../../store/provider/CurrentChatContextProvider';
import { getCurrentUser } from '../../../utils/accountHelper';
import { getTabStore } from '../../../utils/store/getStore';

function uniqueMessages(arr: WsMessage[]): WsMessage[] {
    return [...new Map(arr.map((m) => [m.id, m])).values()]; // returns random-ordered array
}

export default function useChatMessages(
    chatSessionId: string,
    tabKey: string,
    audioEl: MutableRefObject<HTMLAudioElement>
) {
    const { asPath } = useRouter();
    const {
        data: {
            userInfo: { userId }
        }
    } = useSession();
    const [messages, setMessages] = useState<WsMessage[]>([]);
    const dispatch = useCurrentChatDispatch();

    const selectedTabKey = getSelectedTabKeyFromUrl(asPath);
    const isCurrentTab = selectedTabKey === tabKey;

    useEffect(() => {
        isCurrentTab && getTabStore().resetMessageCount(tabKey);
    }, [isCurrentTab, tabKey]);

    const incomingMessageHandler = useCallback(
        (event: CustomEventInit<WsMessage>) => {
            const message = event.detail;
            setMessages((prevState) => [...prevState, message]);

            getTabStore().updateChatTicketTab({
                propertyKey: 'description',
                value: getFileNameOrTextMessage(message.text),
                tabKey,
                increaseCountFlag: !isCurrentTab
            });

            if (audioEl.current && 'senderId' in message && message?.senderId !== getCurrentUser().id) {
                audioEl.current.pause();
                audioEl.current.currentTime = 0; // to start over again
                audioEl.current.play();
            }
        },
        [audioEl, isCurrentTab, tabKey]
    );

    const historyHandler = useCallback((event: CustomEventInit<WsMessage[]>) => {
        const messagesFromHistory = event.detail?.map((m) => ({
            ...m,
            id: m.msgGrispiId,
            msgGrispiId: undefined,
            fromHistory: true
        }));
        setMessages((prevMessages) => {
            const mergedMessages = [...messagesFromHistory, ...prevMessages];
            const uniqueMessagesUnsorted = uniqueMessages(mergedMessages);
            uniqueMessagesUnsorted.sort((a, b) => a.sentAt - b.sentAt);
            return uniqueMessagesUnsorted;
        });
    }, []);

    const markMessageAsSentHandler = useCallback((event: any) => {
        const receiptId = event.detail;
        setMessages((prev) => {
            return prev.map((prevMessage) => {
                if (prevMessage.receiptId === receiptId) {
                    return {
                        ...prevMessage,
                        status: 'sent',
                        id: receiptId,

                        // Once we update the id, we need to set the senderId to know that this message is sent by us. (isMine check in MessageList)
                        // If the message has already a senderId then it means that the message is welcome or dispatch message, so we should not change the senderId
                        senderId: prevMessage.senderId == null ? getCurrentUser()?.id : prevMessage.senderId
                    };
                } else {
                    return prevMessage;
                }
            });
        });
    }, []);

    const markMessageAsReceivedHandler = useCallback((event: any) => {
        const messageId = event.detail;
        setMessages((prev) => {
            return prev.map((prevMessage) => {
                if (prevMessage.id === messageId) {
                    return {
                        ...prevMessage,
                        status: 'received',
                        receivedAt: Date.now()
                    };
                } else {
                    return prevMessage;
                }
            });
        });
    }, []);

    const markMessageAsSeenHandler = useCallback(
        (event: any) => {
            const { messageId, seenAt }: { messageId: string; seenAt: number } = event.detail;
            setMessages((prev) => {
                return prev.map((prevMessage) => {
                    if (prevMessage.id === messageId || (!prevMessage.seenAt && prevMessage.senderId === userId)) {
                        return {
                            ...prevMessage,
                            status: 'read',
                            // sometimes received message arrives after seen message. That overrides the 'read' status to be 'received'
                            // adding seenAt property on the message ensures correct status handling in MessageList#messageToBox function
                            seenAt: seenAt ?? Date.now()
                        };
                    } else {
                        return prevMessage;
                    }
                });
            });
        },
        [userId]
    );

    useEffect(() => {
        window.addEventListener(`${InternalEventType.CHAT_HISTORY_READY}-${tabKey}`, historyHandler);
        if (chatSessionId) {
            window.addEventListener(`${InternalEventType.INCOMING_MESSAGE}-${chatSessionId}`, incomingMessageHandler);
            window.addEventListener(`${InternalEventType.CHAT_HISTORY_READY}-${chatSessionId}`, historyHandler);
            window.addEventListener(`${InternalEventType.GOT_RECEIPT}-${chatSessionId}`, markMessageAsSentHandler);
            window.addEventListener(
                `${InternalEventType.MESSAGE_RECEIVED}-${chatSessionId}`,
                markMessageAsReceivedHandler
            );
            window.addEventListener(`${InternalEventType.MESSAGE_SEEN}-${chatSessionId}`, markMessageAsSeenHandler);
        }
        return () => {
            window.removeEventListener(
                `${InternalEventType.INCOMING_MESSAGE}-${chatSessionId}`,
                incomingMessageHandler
            );
            window.removeEventListener(`${InternalEventType.CHAT_HISTORY_READY}-${chatSessionId}`, historyHandler);
            window.removeEventListener(`${InternalEventType.CHAT_HISTORY_READY}-${tabKey}`, historyHandler);
            window.removeEventListener(`${InternalEventType.GOT_RECEIPT}-${chatSessionId}`, markMessageAsSentHandler);
            window.removeEventListener(
                `${InternalEventType.MESSAGE_RECEIVED}-${chatSessionId}`,
                markMessageAsReceivedHandler
            );
            window.removeEventListener(`${InternalEventType.MESSAGE_SEEN}-${chatSessionId}`, markMessageAsSeenHandler);
        };
    }, [
        chatSessionId,
        historyHandler,
        incomingMessageHandler,
        markMessageAsReceivedHandler,
        markMessageAsSeenHandler,
        markMessageAsSentHandler,
        tabKey
    ]);

    useEffect(() => {
        isCurrentTab && dispatch({ type: 'SET_MESSAGES', payload: messages });
    }, [isCurrentTab, messages, dispatch]);

    return { messages };
}
